import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import {
  PlusIcon,
  TrashIcon,
  VideoCameraIcon,
  ArrowTopRightOnSquareIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  Cog6ToothIcon,
  KeyIcon,
  BoltIcon,
  GlobeAltIcon,
  FilmIcon,
} from '@heroicons/react/24/outline';
import { youtubeApi, YouTubeAccount, YouTubeUpload } from '@/services/youtubeApi';
import { integrationsApi, IntegrationsData, IntegrationStatus } from '@/services/integrationsApi';
import { socialApi, SocialAccount, SocialPlatform } from '@/services/socialApi';
import brandingApi from '@/services/brandingApi';
import SocialPublishModal from '@/components/Video/SocialPublishModal';
import Pagination, { paginate } from '@/components/Common/Pagination';

const ITEMS_PER_PAGE = 10;

const IntegrationsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<'youtube' | 'uploads' | 'settings' | 'social' | 'branding' | 'elevenlabs' | 'sarvam'>('youtube');
  const [introFile, setIntroFile] = useState<File | null>(null);
  const [outroFile, setOutroFile] = useState<File | null>(null);
  const [elevenLabsKey, setElevenLabsKey] = useState('');
  const [sarvamKey, setSarvamKey] = useState('');
  const [facebookPostComposerOpen, setFacebookPostComposerOpen] = useState(false);
  const [accountsPage, setAccountsPage] = useState(1);
  const [uploadsPage, setUploadsPage] = useState(1);

  const { data: configData, isLoading: configLoading } = useQuery({
    queryKey: ['youtube-config'],
    queryFn: () => youtubeApi.getConfig(),
  });

  const { data: accountsData, isLoading: accountsLoading } = useQuery({
    queryKey: ['youtube-accounts'],
    queryFn: () => youtubeApi.getAccounts(),
  });

  const { data: uploadsData, isLoading: uploadsLoading } = useQuery({
    queryKey: ['youtube-uploads'],
    queryFn: () => youtubeApi.getUploads(),
    enabled: activeTab === 'uploads',
  });

  const { data: integrationsData, isLoading: integrationsLoading } = useQuery({
    queryKey: ['integrations'],
    queryFn: () => integrationsApi.getIntegrations(),
  });

  const { data: socialConfigData, isLoading: socialConfigLoading } = useQuery({
    queryKey: ['social-config'],
    queryFn: () => socialApi.getConfig(),
  });

  const { data: socialAccountsData, isLoading: socialAccountsLoading } = useQuery({
    queryKey: ['social-accounts'],
    queryFn: () => socialApi.getAccounts(),
  });

  const { data: brandingData, isLoading: brandingLoading } = useQuery({
    queryKey: ['org-branding'],
    queryFn: () => brandingApi.get(),
    enabled: activeTab === 'branding',
  });

  const configMutation = useMutation({
    mutationFn: (enabled: boolean) => youtubeApi.updateConfig(enabled),
    onSuccess: () => {
      toast.success('YouTube configuration updated');
      queryClient.invalidateQueries({ queryKey: ['youtube-config'] });
    },
    onError: () => toast.error('Failed to update YouTube configuration'),
  });

  const connectMutation = useMutation({
    mutationFn: () => youtubeApi.getAuthUrl(),
    onSuccess: (data) => {
      if (data.url) {
        window.location.href = data.url;
      }
    },
    onError: () => toast.error('Failed to start YouTube connection'),
  });

  const unlinkMutation = useMutation({
    mutationFn: (id: string) => youtubeApi.unlinkAccount(id),
    onSuccess: () => {
      toast.success('YouTube account unlinked');
      queryClient.invalidateQueries({ queryKey: ['youtube-accounts'] });
    },
    onError: () => toast.error('Failed to unlink account'),
  });

  const socialConnectMutation = useMutation({
    mutationFn: () => socialApi.getAuthUrl(),
    onSuccess: (data) => {
      window.location.assign(data.url);
    },
    onError: (error: any) => toast.error(error?.response?.data?.message || 'Failed to start Meta connection'),
  });

  const socialUnlinkMutation = useMutation({
    mutationFn: (accountId: string) => socialApi.unlinkAccount(accountId),
    onSuccess: () => {
      toast.success('Social channel unlinked');
      queryClient.invalidateQueries({ queryKey: ['social-accounts'] });
    },
    onError: () => toast.error('Failed to unlink social channel'),
  });

  const uploadIntroMutation = useMutation({
    mutationFn: (file: File) => brandingApi.uploadIntro(file),
    onSuccess: (res) => {
      if (res.success) {
        toast.success('Intro uploaded');
        setIntroFile(null);
        queryClient.invalidateQueries({ queryKey: ['org-branding'] });
      } else {
        toast.error(res.message || 'Failed to upload intro');
      }
    },
    onError: (err: any) => toast.error(err?.response?.data?.message || 'Failed to upload intro'),
  });

  const uploadOutroMutation = useMutation({
    mutationFn: (file: File) => brandingApi.uploadOutro(file),
    onSuccess: (res) => {
      if (res.success) {
        toast.success('Outro uploaded');
        setOutroFile(null);
        queryClient.invalidateQueries({ queryKey: ['org-branding'] });
      } else {
        toast.error(res.message || 'Failed to upload outro');
      }
    },
    onError: (err: any) => toast.error(err?.response?.data?.message || 'Failed to upload outro'),
  });

  const removeIntroMutation = useMutation({
    mutationFn: () => brandingApi.removeIntro(),
    onSuccess: () => {
      toast.success('Intro removed');
      queryClient.invalidateQueries({ queryKey: ['org-branding'] });
    },
    onError: (err: any) => toast.error(err?.response?.data?.message || 'Failed to remove intro'),
  });

  const removeOutroMutation = useMutation({
    mutationFn: () => brandingApi.removeOutro(),
    onSuccess: () => {
      toast.success('Outro removed');
      queryClient.invalidateQueries({ queryKey: ['org-branding'] });
    },
    onError: (err: any) => toast.error(err?.response?.data?.message || 'Failed to remove outro'),
  });

  const integrationsMutation = useMutation({
    mutationFn: (data: { elevenLabsApiKey?: string | null; sarvamApiKey?: string | null }) =>
      integrationsApi.updateIntegrations(data),
    onSuccess: () => {
      toast.success('Integration settings updated');
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
      setElevenLabsKey('');
      setSarvamKey('');
    },
    onError: () => toast.error('Failed to update integration settings'),
  });

  const testIntegrationsMutation = useMutation({
    mutationFn: () => integrationsApi.testIntegrations(),
    onSuccess: (data) => {
      const { elevenLabs, sarvam } = data.data || {};
      if (elevenLabs?.valid) toast.success('ElevenLabs connection verified');
      else if (elevenLabs?.message) toast.error(`ElevenLabs: ${elevenLabs.message}`);
      if (sarvam?.valid) toast.success('Sarvam connection verified');
      else if (sarvam?.message) toast.error(`Sarvam: ${sarvam.message}`);
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
    },
    onError: () => toast.error('Failed to test integrations'),
  });

  const accounts = accountsData?.accounts || [];
  const uploads = uploadsData?.uploads || [];
  const integrations = integrationsData?.data as IntegrationsData | undefined;
  const socialAccounts = socialAccountsData?.accounts || [];
  const paginatedAccounts = paginate(accounts, accountsPage, ITEMS_PER_PAGE);
  const paginatedUploads = paginate(uploads, uploadsPage, ITEMS_PER_PAGE);

  return (
    <div className="p-6">
      <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">Integrations</h1>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="flex -mb-px">
            <button
              onClick={() => setActiveTab('youtube')}
              className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'youtube'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              <span className="flex items-center gap-2">
                <VideoCameraIcon className="h-5 w-5" />
                YouTube
              </span>
            </button>
            <button
              onClick={() => setActiveTab('uploads')}
              className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'uploads'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              <span className="flex items-center gap-2">
                <ArrowTopRightOnSquareIcon className="h-5 w-5" />
                Published Videos
              </span>
            </button>
            <button
              onClick={() => setActiveTab('settings')}
              className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'settings'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              <span className="flex items-center gap-2">
                <Cog6ToothIcon className="h-5 w-5" />
                YouTube Settings
              </span>
            </button>
            <button
              onClick={() => setActiveTab('social')}
              className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'social'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              <span className="flex items-center gap-2">
                <GlobeAltIcon className="h-5 w-5" />
                Social Channels
              </span>
            </button>
            <button
              onClick={() => setActiveTab('branding')}
              className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'branding'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              <span className="flex items-center gap-2">
                <FilmIcon className="h-5 w-5" />
                Branding
              </span>
            </button>
            <button
              onClick={() => setActiveTab('elevenlabs')}
              className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'elevenlabs'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              <span className="flex items-center gap-2">
                <BoltIcon className="h-5 w-5" />
                ElevenLabs
              </span>
            </button>
            <button
              onClick={() => setActiveTab('sarvam')}
              className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'sarvam'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              <span className="flex items-center gap-2">
                <GlobeAltIcon className="h-5 w-5" />
                Sarvam
              </span>
            </button>
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'youtube' && (
            <div className="space-y-6">
              {configLoading ? null : (
                <>
                  {!configData?.globallyConfigured && (
                    <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 text-sm text-amber-800 dark:text-amber-200">
                      YouTube OAuth is not globally configured. An administrator must set
                      YOUTUBE_CLIENT_ID and YOUTUBE_CLIENT_SECRET in the server environment before
                      organizations can connect accounts.
                    </div>
                  )}
                  {!configData?.enabled && (
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 text-sm text-red-800 dark:text-red-200">
                      YouTube integration is currently disabled for this organization. Enable it in
                      the Settings tab below.
                    </div>
                  )}
                </>
              )}
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">YouTube Accounts</h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Connect one or more YouTube channels to publish translated videos directly.
                  </p>
                </div>
                <button
                  onClick={() => connectMutation.mutate()}
                  disabled={connectMutation.isLoading || !configData?.enabled || !configData?.globallyConfigured}
                  className="inline-flex items-center px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 disabled:opacity-50 transition-colors"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  Connect YouTube
                </button>
              </div>

              {accountsLoading ? (
                <div className="space-y-3">
                  {[1, 2].map((i) => (
                    <div key={i} className="animate-pulse h-20 bg-gray-100 dark:bg-gray-700 rounded-lg" />
                  ))}
                </div>
              ) : accounts.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 dark:bg-gray-700/30 rounded-lg border border-dashed border-gray-300 dark:border-gray-600">
                  <VideoCameraIcon className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                  <h3 className="text-sm font-medium text-gray-900 dark:text-white">No YouTube accounts linked</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Connect a YouTube channel to start publishing translated videos.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {paginatedAccounts.map((account: YouTubeAccount) => (
                    <div
                      key={account._id}
                      className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/30 rounded-lg border border-gray-200 dark:border-gray-700"
                    >
                      <div className="flex items-center gap-4">
                        {account.channelThumbnailUrl ? (
                          <img
                            src={account.channelThumbnailUrl}
                            alt={account.channelTitle}
                            className="h-12 w-12 rounded-full object-cover"
                          />
                        ) : (
                          <div className="h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                            <VideoCameraIcon className="h-6 w-6 text-red-600 dark:text-red-400" />
                          </div>
                        )}
                        <div>
                          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{account.channelTitle}</h3>
                          <p className="text-xs text-gray-500 dark:text-gray-400">Channel ID: {account.channelId}</p>
                          {account.needsReconnect ? (
                            <span className="inline-flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400 mt-1">
                              <ExclamationCircleIcon className="h-3 w-3" /> Reconnect required
                            </span>
                          ) : account.isActive ? (
                            <span className="inline-flex items-center gap-1 text-xs text-green-600 dark:text-green-400 mt-1">
                              <CheckCircleIcon className="h-3 w-3" /> Active
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-xs text-red-600 dark:text-red-400 mt-1">
                              <ExclamationCircleIcon className="h-3 w-3" /> Inactive
                            </span>
                          )}
                          {account.lastAuthError && (
                            <p className="text-[10px] text-amber-600 dark:text-amber-400 mt-0.5 max-w-xs">
                              {account.lastAuthError}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {account.needsReconnect && (
                          <button
                            onClick={() => connectMutation.mutate()}
                            disabled={connectMutation.isLoading}
                            className="inline-flex items-center px-3 py-1.5 text-sm text-amber-700 hover:text-amber-800 dark:text-amber-400 dark:hover:text-amber-300 border border-amber-200 dark:border-amber-800 hover:bg-amber-50 dark:hover:bg-amber-900/30 rounded-md transition-colors"
                          >
                            {connectMutation.isLoading ? (
                              <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-1.5" />
                            ) : (
                              <ArrowTopRightOnSquareIcon className="h-4 w-4 mr-1.5" />
                            )}
                            Reconnect
                          </button>
                        )}
                        <button
                          onClick={() => {
                            if (window.confirm('Unlink this YouTube account?')) {
                              unlinkMutation.mutate(account._id);
                            }
                          }}
                          disabled={unlinkMutation.isLoading}
                          className="inline-flex items-center px-3 py-1.5 text-sm text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 border border-red-200 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-md transition-colors"
                        >
                          <TrashIcon className="h-4 w-4 mr-1.5" />
                          Unlink
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <Pagination
                currentPage={accountsPage}
                totalPages={Math.ceil(accounts.length / ITEMS_PER_PAGE)}
                onPageChange={setAccountsPage}
                totalItems={accounts.length}
                itemsPerPage={ITEMS_PER_PAGE}
              />
            </div>
          )}

          {activeTab === 'uploads' && (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Published Videos</h2>

              {uploadsLoading ? (
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="animate-pulse h-16 bg-gray-100 dark:bg-gray-700 rounded-lg" />
                  ))}
                </div>
              ) : uploads.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 dark:bg-gray-700/30 rounded-lg border border-dashed border-gray-300 dark:border-gray-600">
                  <ArrowTopRightOnSquareIcon className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                  <h3 className="text-sm font-medium text-gray-900 dark:text-white">No videos published yet</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Published videos from your connected channels will appear here.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {paginatedUploads.map((upload: YouTubeUpload) => (
                    <div
                      key={upload._id}
                      className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/30 rounded-lg border border-gray-200 dark:border-gray-700"
                    >
                      <div>
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{upload.title}</h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {upload.channelTitle} • {new Date(upload.publishedAt).toLocaleString()} •{' '}
                          <span className="capitalize">{upload.privacyStatus}</span>
                        </p>
                      </div>
                      <a
                        href={upload.videoUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-3 py-1.5 text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 border border-blue-200 dark:border-blue-800 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-md transition-colors"
                      >
                        <ArrowTopRightOnSquareIcon className="h-4 w-4 mr-1.5" />
                        View
                      </a>
                    </div>
                  ))}
                </div>
              )}
              <Pagination
                currentPage={uploadsPage}
                totalPages={Math.ceil(uploads.length / ITEMS_PER_PAGE)}
                onPageChange={setUploadsPage}
                totalItems={uploads.length}
                itemsPerPage={ITEMS_PER_PAGE}
              />
            </div>
          )}

          {activeTab === 'settings' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">YouTube Settings</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Enable or disable YouTube integration for this organization.
                </p>
              </div>

              {configLoading ? (
                <div className="animate-pulse h-12 bg-gray-100 dark:bg-gray-700 rounded-lg" />
              ) : (
                <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/30 rounded-lg border border-gray-200 dark:border-gray-700">
                  <div>
                    <h3 className="text-sm font-medium text-gray-900 dark:text-white">Enable YouTube Integration</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      When enabled, organization members can link YouTube accounts and publish translated videos.
                    </p>
                  </div>
                  <button
                    onClick={() => configMutation.mutate(!configData?.enabled)}
                    disabled={configMutation.isLoading}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                      configData?.enabled ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-600'
                    } disabled:opacity-50`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        configData?.enabled ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              )}

              {!configLoading && (
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  <p className="mb-2">
                    <span className="font-medium">Status:</span>{' '}
                    {configData?.globallyConfigured ? (
                      <span className="text-green-600 dark:text-green-400">Globally configured</span>
                    ) : (
                      <span className="text-amber-600 dark:text-amber-400">Not globally configured</span>
                    )}
                  </p>
                  <p>
                    YouTube OAuth credentials are managed at the application level. Each organization links
                    its own channels and controls whether the integration is available to its members.
                  </p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'social' && (
            <div className="space-y-6">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Meta Channels</h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Connect Facebook Pages, Instagram Professional accounts, and WhatsApp Business senders through one secure Meta authorization.</p>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setFacebookPostComposerOpen(true)} className="inline-flex items-center px-4 py-2 border border-blue-200 text-blue-700 text-sm font-medium rounded-md hover:bg-blue-50 dark:border-blue-800 dark:text-blue-400 dark:hover:bg-blue-900/20 transition-colors">Create Facebook post</button>
                  <button onClick={() => socialConnectMutation.mutate()} disabled={socialConnectMutation.isLoading || !socialConfigData?.platforms.some((platform) => platform.configured)} className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors">
                    <PlusIcon className="h-4 w-4 mr-2" />
                    {socialConnectMutation.isLoading ? 'Connecting…' : 'Connect Meta'}
                  </button>
                </div>
              </div>
              {!socialConfigLoading && !socialConfigData?.platforms.some((platform) => platform.configured) && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200">Configure `META_APP_ID`, `META_APP_SECRET`, `META_REDIRECT_URI`, and a server signing secret before connecting Meta channels.</div>
              )}
              <div className="grid gap-4 md:grid-cols-3">
                {(['facebook', 'instagram', 'whatsapp'] as SocialPlatform[]).map((platform) => {
                  const config = socialConfigData?.platforms.find((item) => item.platform === platform);
                  const description = platform === 'whatsapp' ? 'Send text or video messages to eligible recipients.' : platform === 'instagram' ? 'Publish Reels and video posts from generated clips.' : 'Publish Page posts and videos from generated clips.';
                  return (
                    <div key={platform} className="p-4 bg-gray-50 dark:bg-gray-700/30 rounded-lg border border-gray-200 dark:border-gray-700">
                      <div className="flex items-center justify-between">
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-white capitalize">{platform}</h3>
                        {socialConfigLoading ? <span className="text-xs text-gray-500">Checking…</span> : config?.configured ? <span className="inline-flex items-center gap-1 text-xs text-green-600 dark:text-green-400"><CheckCircleIcon className="h-3.5 w-3.5" /> Ready</span> : <span className="inline-flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400"><ExclamationCircleIcon className="h-3.5 w-3.5" /> Setup required</span>}
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">{description}</p>
                    </div>
                  );
                })}
              </div>
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Connected channels</h3>
                {socialAccountsLoading ? <div className="animate-pulse h-20 bg-gray-100 dark:bg-gray-700 rounded-lg" /> : socialAccounts.length === 0 ? (
                  <div className="text-center py-10 bg-gray-50 dark:bg-gray-700/30 rounded-lg border border-dashed border-gray-300 dark:border-gray-600 text-sm text-gray-500 dark:text-gray-400">No Meta channels connected yet.</div>
                ) : socialAccounts.map((account: SocialAccount) => (
                  <div key={account._id} className="flex items-center justify-between gap-4 p-4 bg-gray-50 dark:bg-gray-700/30 rounded-lg border border-gray-200 dark:border-gray-700">
                    <div className="flex items-center gap-3 min-w-0">
                      {account.accountImageUrl ? <img src={account.accountImageUrl} alt="" className="h-10 w-10 rounded-full object-cover" /> : <div className="h-10 w-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center"><GlobeAltIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" /></div>}
                      <div className="min-w-0"><p className="font-medium text-sm text-gray-900 dark:text-white truncate">{account.accountName}</p><p className="text-xs text-gray-500 dark:text-gray-400 capitalize">{account.platform}{account.accountHandle ? ` · ${account.accountHandle}` : ''}</p></div>
                    </div>
                    <button onClick={() => { if (window.confirm(`Unlink ${account.accountName}?`)) socialUnlinkMutation.mutate(account._id); }} disabled={socialUnlinkMutation.isLoading} className="inline-flex items-center px-3 py-1.5 text-sm text-red-600 hover:text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-md disabled:opacity-50"><TrashIcon className="h-4 w-4 mr-1.5" />Unlink</button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'branding' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">Publishing branding</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Upload a default intro and/or outro clip once — they&apos;ll be prepended/appended automatically to
                  every YouTube and social video you publish, unless you opt out or override them for a specific publish.
                </p>
              </div>

              {brandingLoading ? (
                <div className="animate-pulse h-32 bg-gray-100 dark:bg-gray-700 rounded-lg" />
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {(['intro', 'outro'] as const).map((kind) => {
                    const url = kind === 'intro' ? brandingData?.branding?.introUrl : brandingData?.branding?.outroUrl;
                    const file = kind === 'intro' ? introFile : outroFile;
                    const setFile = kind === 'intro' ? setIntroFile : setOutroFile;
                    const uploadMutation = kind === 'intro' ? uploadIntroMutation : uploadOutroMutation;
                    const removeMutation = kind === 'intro' ? removeIntroMutation : removeOutroMutation;
                    return (
                      <div key={kind} className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 space-y-3">
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-300 capitalize">{kind}</p>
                        {url ? (
                          <video src={url} controls className="w-full rounded-md bg-black" style={{ maxHeight: 200 }} />
                        ) : (
                          <p className="text-xs text-gray-400 dark:text-gray-500">No {kind} configured yet.</p>
                        )}
                        <div className="flex items-center gap-2">
                          <label className="flex items-center gap-2 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-sm text-gray-700 dark:text-gray-300">
                            {file ? file.name : `Choose ${kind} video`}
                            <input
                              type="file"
                              accept="video/*"
                              className="hidden"
                              onChange={(e) => setFile(e.target.files?.[0] || null)}
                            />
                          </label>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => file && uploadMutation.mutate(file)}
                            disabled={!file || uploadMutation.isLoading}
                            className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50"
                          >
                            {uploadMutation.isLoading ? 'Uploading…' : `Upload ${kind}`}
                          </button>
                          {url && (
                            <button
                              type="button"
                              onClick={() => removeMutation.mutate()}
                              disabled={removeMutation.isLoading}
                              className="px-3 py-1.5 text-sm font-medium text-red-600 hover:text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-md disabled:opacity-50"
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {activeTab === 'elevenlabs' && (
            <ApiKeyIntegrationTab
              title="ElevenLabs"
              icon={<BoltIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" />}
              description="Connect your organization's ElevenLabs account to enable voice cloning, text-to-speech, and audio translation."
              status={integrations?.elevenLabs}
              apiKey={elevenLabsKey}
              setApiKey={setElevenLabsKey}
              isLoading={integrationsLoading}
              isSaving={integrationsMutation.isLoading}
              onSave={() => integrationsMutation.mutate({ elevenLabsApiKey: elevenLabsKey || null })}
              onDisconnect={() => integrationsMutation.mutate({ elevenLabsApiKey: null })}
              onTest={() => testIntegrationsMutation.mutate()}
              isTesting={testIntegrationsMutation.isLoading}
            />
          )}

          {activeTab === 'sarvam' && (
            <ApiKeyIntegrationTab
              title="Sarvam"
              icon={<GlobeAltIcon className="h-6 w-6 text-orange-600 dark:text-orange-400" />}
              description="Connect your organization's Sarvam account to enable speech-to-text, translation, and Indic language features."
              status={integrations?.sarvam}
              apiKey={sarvamKey}
              setApiKey={setSarvamKey}
              isLoading={integrationsLoading}
              isSaving={integrationsMutation.isLoading}
              onSave={() => integrationsMutation.mutate({ sarvamApiKey: sarvamKey || null })}
              onDisconnect={() => integrationsMutation.mutate({ sarvamApiKey: null })}
              onTest={() => testIntegrationsMutation.mutate()}
              isTesting={testIntegrationsMutation.isLoading}
            />
          )}
        </div>
      </div>
      {facebookPostComposerOpen && (
        <SocialPublishModal
          sourceType="text"
          onClose={() => setFacebookPostComposerOpen(false)}
          onPublished={() => setFacebookPostComposerOpen(false)}
        />
      )}
    </div>
  );
};

interface ApiKeyIntegrationTabProps {
  title: string;
  icon: React.ReactNode;
  description: string;
  status?: IntegrationStatus;
  apiKey: string;
  setApiKey: (value: string) => void;
  isLoading: boolean;
  isSaving: boolean;
  onSave: () => void;
  onDisconnect: () => void;
  onTest: () => void;
  isTesting: boolean;
}

const ApiKeyIntegrationTab: React.FC<ApiKeyIntegrationTabProps> = ({
  title,
  icon,
  description,
  status,
  apiKey,
  setApiKey,
  isLoading,
  isSaving,
  onSave,
  onDisconnect,
  onTest,
  isTesting,
}) => {
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        {icon}
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{description}</p>
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          <div className="animate-pulse h-12 bg-gray-100 dark:bg-gray-700 rounded-lg" />
          <div className="animate-pulse h-12 bg-gray-100 dark:bg-gray-700 rounded-lg" />
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/30 rounded-lg border border-gray-200 dark:border-gray-700">
            <div>
              <h3 className="text-sm font-medium text-gray-900 dark:text-white">Connection Status</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {status?.connected
                  ? `Connected with API key ${status.apiKey || 'configured'}`
                  : 'Not connected. Add your API key below.'}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {status?.connected ? (
                <span className="inline-flex items-center gap-1 text-xs text-green-600 dark:text-green-400">
                  <CheckCircleIcon className="h-3 w-3" /> Connected
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                  <ExclamationCircleIcon className="h-3 w-3" /> Not connected
                </span>
              )}
            </div>
          </div>

          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              API Key
            </label>
            <div className="flex gap-3">
              <input
                type="password"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder={status?.connected ? 'Enter new API key to update' : 'Enter your API key'}
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {apiKey && (
                <button
                  onClick={() => setApiKey('')}
                  disabled={isSaving}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 transition-colors"
                >
                  Cancel
                </button>
              )}
              <button
                onClick={onSave}
                disabled={isSaving || !apiKey.trim()}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                <KeyIcon className="h-4 w-4 mr-2" />
                Save
              </button>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={onTest}
              disabled={isTesting || !status?.connected}
              className="inline-flex items-center px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 transition-colors"
            >
              {isTesting ? 'Testing...' : 'Test Connection'}
            </button>
            {status?.connected && (
              <button
                onClick={onDisconnect}
                disabled={isSaving}
                className="inline-flex items-center px-4 py-2 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-900/30 text-sm font-medium rounded-md disabled:opacity-50 transition-colors"
              >
                Disconnect
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default IntegrationsPage;
